import { useEffect, useState, useCallback } from "react";
import api, { fmtDateTime, formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { UserPlus, KeyRound, Trash2, UserCheck, Power, LayoutGrid, Link2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { managerNav } from "@/lib/navConfig";
import { toast } from "sonner";

const MODULES = managerNav.filter((m) => m.to !== "/manager");

function ModulesDialog({ m, companyModules, onClose, onSaved }) {
  const [mods, setMods] = useState({});
  useEffect(() => { setMods(m?.manager_modules || {}); }, [m]);
  const save = async () => {
    try { await api.put(`/managers/${m.id}/modules`, { modules: mods }); toast.success("Modules updated"); onSaved(); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!m} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="manager-modules-dialog">
        <DialogHeader><DialogTitle>Modules for {m?.name}</DialogTitle><DialogDescription>Switch modules off for this Manager only. Modules switched off for the whole company stay off.</DialogDescription></DialogHeader>
        <div className="space-y-2">
          {MODULES.map((x) => {
            const companyOff = companyModules?.[x.to] === false;
            return (
              <label key={x.to} className={`flex items-center justify-between border border-zinc-200 rounded-md px-3 py-2 text-sm ${companyOff ? "opacity-50" : ""}`}>
                <span className="flex items-center gap-2"><x.icon className="h-4 w-4 text-zinc-500" />{x.label}{companyOff && <span className="text-[10px] text-zinc-500">(off for company)</span>}</span>
                <Switch data-testid={`mgr-mod-${x.to.split("/").pop()}`} disabled={companyOff} checked={!companyOff && mods[x.to] !== false} onCheckedChange={(v) => setMods({ ...mods, [x.to]: v })} />
              </label>
            );
          })}
          <Button data-testid="mgr-mod-save" onClick={save} className="w-full bg-slate-900 text-white">Save modules</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const BLANK = { name: "", email: "", password: "", employee_id: "" };

function EmployeeSelect({ emps, value, onChange, testid }) {
  return (
    <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm">
      <option value="">No employee record (no clock in / timesheets)</option>
      {emps.map((e) => <option key={e.id} value={e.id}>{e.first_name} {e.surname}{e.employee_number ? ` (${e.employee_number})` : ""}</option>)}
    </select>
  );
}

export function ManagersPanel({ cid, impersonate, nav, companyModules }) {
  const [rows, setRows] = useState([]);
  const [modsFor, setModsFor] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [emps, setEmps] = useState([]);
  const [linkFor, setLinkFor] = useState(null);
  useEffect(() => { api.get("/employees", { headers: { "x-company-id": cid } }).then((r) => setEmps(r.data.filter((e) => e.active !== false))).catch(() => {}); }, [cid]);
  const empName = (id) => { const e = emps.find((x) => x.id === id); return e ? `${e.first_name} ${e.surname}` : null; };
  const load = useCallback(() => api.get(`/companies/${cid}/managers`).then((r) => setRows(r.data)).catch(() => {}), [cid]);
  useEffect(() => { load(); }, [load]);
  const err = (e) => toast.error(formatApiError(e.response?.data?.detail));

  const create = async () => {
    try { await api.post(`/companies/${cid}/managers`, form); toast.success("Manager login created"); setOpen(false); setForm(BLANK); load(); } catch (e) { err(e); }
  };
  const saveLink = async () => {
    try { await api.put(`/managers/${linkFor.id}/employee`, { employee_id: linkFor.employee_id || null }); toast.success("Employee link saved"); setLinkFor(null); load(); } catch (e) { err(e); }
  };
  const toggle = async (m) => { try { await api.put(`/managers/${m.id}/active`, { active: !m.active }); load(); } catch (e) { err(e); } };
  const resetPw = async (m) => {
    const pw = window.prompt(`New temporary password for ${m.name} (min 8 characters):`);
    if (!pw) return;
    try { await api.post(`/managers/${m.id}/reset-password`, { password: pw }); toast.success("Password updated"); } catch (e) { err(e); }
  };
  const remove = async (m) => { if (!window.confirm(`Remove manager login for ${m.name}?`)) return; try { await api.delete(`/managers/${m.id}`); toast.success("Manager removed"); load(); } catch (e) { err(e); } };
  const enter = async (m) => { try { await impersonate(m.id); toast.success("Viewing as manager"); nav("/manager"); } catch (e) { err(e); } };

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-4">
        <p className="text-sm text-zinc-500 max-w-2xl">Managers see Employees and Subcontractors (names and categories only), Compliance, Resources, Forms & Checklists, Sites (view only) and the Leave Calendar. They never see pay, personal details, customers, finance, approvals, job roles or reports. Link a Manager to their employee record so they can also clock in, submit timesheets and request leave (approved by company Admins).</p>
        <Button data-testid="add-manager-btn" onClick={() => setOpen(true)} className="bg-slate-900 text-white shrink-0"><UserPlus className="h-4 w-4 mr-1" />Add Manager</Button>
      </div>
      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs font-semibold uppercase tracking-wider text-zinc-500 border-b border-zinc-200"><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Employee record</th><th className="p-3">Role</th><th className="p-3">Last login</th><th className="p-3">Status</th><th className="p-3" /></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-zinc-400" data-testid="managers-empty">No manager logins yet.</td></tr>}
            {rows.map((m) => (
              <tr key={m.id} data-testid={`manager-row-${m.id}`} className="border-b border-zinc-50">
                <td className="p-3 font-medium">{m.name}</td>
                <td className="p-3 text-zinc-600">{m.email}</td>
                <td className="p-3 text-xs" data-testid={`manager-emp-${m.id}`}>{m.employee_id ? (empName(m.employee_id) || "Linked") : <span className="text-zinc-400">Not linked</span>}</td>
                <td className="p-3"><span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">Manager</span></td>
                <td className="p-3 text-xs text-zinc-500">{m.last_login ? fmtDateTime(m.last_login) : "Never"}</td>
                <td className="p-3"><span data-testid={`manager-status-${m.id}`} className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${m.active ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>{m.active ? "Active" : "Inactive"}</span></td>
                <td className="p-3"><div className="flex gap-1 justify-end">
                  <Button data-testid={`manager-link-${m.id}`} size="sm" variant="outline" className="h-8" title="Link employee record" onClick={() => setLinkFor({ ...m })}><Link2 className="h-3.5 w-3.5" /></Button>
                  <Button data-testid={`manager-modules-${m.id}`} size="sm" variant="outline" className="h-8" title="Modules" onClick={() => setModsFor(m)}><LayoutGrid className="h-3.5 w-3.5" /></Button>
                  <Button data-testid={`manager-enter-${m.id}`} size="sm" variant="outline" className="h-8" title="View as this manager" disabled={!m.active} onClick={() => enter(m)}><UserCheck className="h-3.5 w-3.5" /></Button>
                  <Button data-testid={`manager-reset-${m.id}`} size="sm" variant="outline" className="h-8" title="Reset password" onClick={() => resetPw(m)}><KeyRound className="h-3.5 w-3.5" /></Button>
                  <Button data-testid={`manager-toggle-${m.id}`} size="sm" variant="outline" className="h-8" title={m.active ? "Deactivate" : "Reactivate"} onClick={() => toggle(m)}><Power className="h-3.5 w-3.5" /></Button>
                  <Button data-testid={`manager-delete-${m.id}`} size="sm" variant="outline" className="h-8 text-rose-600 border-rose-200" title="Remove" onClick={() => remove(m)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ModulesDialog m={modsFor} companyModules={companyModules} onClose={() => setModsFor(null)} onSaved={load} />
      <Dialog open={!!linkFor} onOpenChange={(o) => !o && setLinkFor(null)}>
        <DialogContent className="max-w-sm" data-testid="manager-link-dialog">
          <DialogHeader><DialogTitle>Employee record for {linkFor?.name}</DialogTitle><DialogDescription>Linked Managers get a "My work" switch for clock in/out, timesheets, leave and their own forms.</DialogDescription></DialogHeader>
          <EmployeeSelect emps={emps} value={linkFor?.employee_id} onChange={(v) => setLinkFor({ ...linkFor, employee_id: v })} testid="manager-link-select" />
          <Button data-testid="manager-link-save" onClick={saveLink} className="w-full bg-slate-900 text-white">Save</Button>
        </DialogContent>
      </Dialog>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Add Manager</DialogTitle><DialogDescription>They sign in on the normal login page with this email and temporary password.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {[["name", "Full name", "text"], ["email", "Email", "email"], ["password", "Temporary password (min 8)", "text"]].map(([k, l, t]) => (
              <div key={k}><Label>{l}</Label><Input data-testid={`manager-${k}`} type={t} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1" /></div>
            ))}
            <div><Label>Employee record</Label><EmployeeSelect emps={emps} value={form.employee_id} onChange={(v) => setForm({ ...form, employee_id: v })} testid="manager-employee" /></div>
            <Button data-testid="manager-create" onClick={create} className="w-full bg-slate-900 text-white">Create Manager login</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
